import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Home, User, Camera, Users, Calendar, Gem, LogOut, LogIn, X, Palette, UserCheck } from "lucide-react";
import { useLocation } from "wouter";

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function Sidebar({ isOpen, onClose }: SidebarProps) {
  const [location, setLocation] = useLocation();

  const navigateAndClose = (path: string) => {
    setLocation(path);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 z-40" 
        onClick={onClose}
        data-testid="sidebar-overlay"
      />
      
      {/* Sidebar */}
      <div className="fixed inset-y-0 left-0 w-80 bg-dark-base transform transition-transform duration-300 ease-in-out z-50" data-testid="sidebar">
        <div className="flex flex-col h-full">
          {/* Sidebar Header */}
          <div className="p-6 border-b border-gray-700">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-neon-green" data-testid="sidebar-title">LPFTS WORLD WIDE™</h2>
              <Button variant="ghost" size="sm" onClick={onClose} className="text-gray-400" data-testid="button-close-sidebar">
                <X size={20} />
              </Button>
            </div>

          </div>
          
          {/* Navigation Links */}
          <nav className="flex-1 p-6">
            <ul className="space-y-4">
              <li>
                <Button
                  variant={location === '/' ? 'default' : 'ghost'}
                  onClick={() => navigateAndClose('/')}
                  className={`flex items-center w-full justify-start ${
                    location === '/' ? 'bg-neon-green/20 text-neon-green' : 'hover:bg-dark-elevated'
                  }`}
                  data-testid="nav-home"
                >
                  <Home className="mr-3" size={20} />
                  <span>Home</span>
                </Button>
              </li>
              <li>
                <Button
                  variant="ghost"
                  onClick={() => navigateAndClose('/profile')}
                  className={`flex items-center w-full justify-start ${
                    location === '/profile' ? 'bg-neon-green/20 text-neon-green' : 'hover:bg-dark-elevated'
                  }`}
                  data-testid="nav-profile"
                >
                  <User className="mr-3" size={20} />
                  <span>Profile</span>
                </Button>
              </li>
              <li>
                <Button
                  variant="ghost"
                  onClick={() => navigateAndClose('/profile')}
                  className="flex items-center w-full justify-start hover:bg-dark-elevated"
                  data-testid="nav-discoveries"
                >
                  <Camera className="mr-3" size={20} />
                  <span>My Discoveries</span>
                </Button>
              </li>
              <li>
                <Button
                  variant="ghost"
                  onClick={() => navigateAndClose('/community')}
                  className={`flex items-center w-full justify-start ${
                    location === '/community' ? 'bg-neon-green/20 text-neon-green' : 'hover:bg-dark-elevated'
                  }`}
                  data-testid="nav-community"
                >
                  <Users className="mr-3" size={20} />
                  <span>Community</span>
                </Button>
              </li>
              <li>
                <Button
                  variant="ghost"
                  onClick={() => navigateAndClose('/missions')}
                  className={`flex items-center w-full justify-start ${
                    location === '/missions' ? 'bg-neon-green/20 text-neon-green' : 'hover:bg-dark-elevated'
                  }`}
                  data-testid="nav-missions"
                >
                  <Calendar className="mr-3" size={20} />
                  <span>Events & Missions</span>
                </Button>
              </li>
              <li>
                <Button
                  variant="ghost"
                  onClick={() => navigateAndClose('/color-chart')}
                  className={`flex items-center w-full justify-start ${
                    location === '/color-chart' ? 'bg-neon-green/20 text-neon-green' : 'hover:bg-dark-elevated'
                  }`}
                  data-testid="nav-color-chart"
                >
                  <Palette className="mr-3" size={20} />
                  <span>Color Chart</span>
                </Button>
              </li>
              <li>
                <Button
                  variant="ghost"
                  onClick={() => navigateAndClose('/artists')}
                  className={`flex items-center w-full justify-start ${
                    location === '/artists' ? 'bg-neon-green/20 text-neon-green' : 'hover:bg-dark-elevated'
                  }`}
                  data-testid="nav-artists"
                >
                  <UserCheck className="mr-3" size={20} />
                  <span>Featured Artists</span>
                </Button>
              </li>
              <li>
                <Button
                  variant="ghost"
                  onClick={() => navigateAndClose('/profile')}
                  className="flex items-center w-full justify-start hover:bg-dark-elevated"
                  data-testid="nav-nfts"
                >
                  <Gem className="mr-3" size={20} />
                  <span>NFT Collection</span>
                </Button>
              </li>
            </ul>
          </nav>
          
          {/* Auth Section */}
          <div className="p-6 border-t border-gray-700 space-y-3">
            <Button
              variant="ghost"
              onClick={() => {
                onClose();
                window.location.href = '/api/login';
              }}
              className="flex items-center w-full justify-start hover:bg-neon-green/10 text-neon-green"
              data-testid="button-login"
            >
              <LogIn className="mr-3" size={20} />
              <span>Sign In</span>
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                onClose();
                window.location.href = '/api/logout';
              }}
              className="flex items-center w-full justify-start hover:bg-red-900/20 text-red-400"
              data-testid="button-logout"
            >
              <LogOut className="mr-3" size={20} />
              <span>Sign Out</span>
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
